import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePipelineBoardSettings } from '../../pipelineBoardSettings/usePipelineBoardSettings';
import { useAddRenderComponents } from '../../render/renderComponents/useAddRenderComponent';
import AggregationFooter from './AggregationFooter';
import { CreateAggregators } from './CreateAggregators';

/**
 * Takes an array of Array<{[stageId]:Results}>
 * converts it to Array<{[stageId]:Results[]}>
 */
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const toStageAggregationResultCollection = results => {
  return results.reduce((aggregations, result) => {
    Object.keys(result).forEach(stage => {
      aggregations[stage] = aggregations[stage] || {};
      aggregations[stage][result[stage].aggregatorId] = result[stage];
    });
    return aggregations;
  }, {});
};
const StoreContext = /*#__PURE__*/createContext(undefined);
export const AggregationProvider = ({
  children
}) => {
  const pipelineSettings = usePipelineBoardSettings();
  const [aggregators, setAggregators] = useState([]);
  //creates and aggregation collection for context
  const aggregatorCollection = useMemo(() => {
    if (aggregators.length === 0) {
      return undefined;
    }
    return {
      query: options => {
        return aggregators.map(agg => agg.query(options));
      },
      handleResult: queryResult => {
        const results = aggregators.map(agg => agg.handleResult(queryResult));
        return toStageAggregationResultCollection(results);
      },
      reconcile: (aggregations, data, reconciledData) => {
        //first must break apart the collection of results
        //we can use the aggregators index to pick the result we need for a given stage
        //convert this to a result object for consumption by the aggregator
        const stages = Object.keys(aggregations);
        const results = aggregators.map(agg => {
          const baseResult = {};
          stages.forEach(stage => {
            baseResult[stage] = aggregations[stage][agg.id];
          });
          return agg.reconcile(baseResult, data, reconciledData);
        });
        return toStageAggregationResultCollection(results);
      },
      getText: props => {
        if (!props) {
          return [];
        }
        return aggregators.map(agg => {
          return agg.getText(props.aggregation ? props.aggregation[agg.id] : undefined, props);
        });
      },
      getTooltipText: props => {
        if (!props) {
          return [];
        }
        return aggregators.filter(x => x.getTooltipText).map(agg => {
          return agg.getTooltipText(props.aggregation ? props.aggregation[agg.id] : undefined, props);
        });
      }
    };
  }, [aggregators]);
  const handlerAggregatorsReady = aggs => {
    setAggregators(aggs);
  };

  //setup renders based on agg collection
  const render = useCallback(columnData => {
    if (!aggregatorCollection) {
      return /*#__PURE__*/_jsx(_Fragment, {});
    }
    const texts = aggregatorCollection.getText(columnData);
    const tooltip = aggregatorCollection.getTooltipText(columnData);
    return /*#__PURE__*/_jsx(AggregationFooter, {
      texts: texts,
      tooltipTexts: tooltip,
      columnData: columnData
    });
  }, [aggregatorCollection]);
  const renderFooter = aggregatorCollection && pipelineSettings && pipelineSettings.summaryPosition === 'FOOTER' ? render : undefined;
  const renderHeaderSummary = aggregatorCollection && pipelineSettings && pipelineSettings.summaryPosition === 'HEADER' ? render : undefined;
  const addComponent = useAddRenderComponents();
  useEffect(() => {
    addComponent([{
      key: 'renderFooter',
      renderComponent: renderFooter
    }, {
      key: 'renderHeader',
      renderComponent: renderHeaderSummary
    }]);
  }, [renderFooter, renderHeaderSummary, addComponent]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CreateAggregators, {
      aggregatorsReady: handlerAggregatorsReady
    }), /*#__PURE__*/_jsx(StoreContext.Provider, {
      value: aggregatorCollection,
      children: children
    })]
  });
};
export const useAggregationCollection = () => {
  return useContext(StoreContext);
};